import Axios from "../config/axios-v1-config";
import i18n from "../i18n/index.js";
import { apiErrorMessage, apiErrorStatus } from "../utils/apiErrorMessage";

/**
 * Conexión del OPERADOR con su cuenta Bsale (microservice-integration, vía el
 * proxy `integrations/*` del BFF).
 *
 * El operador sale del JWT en el backend: acá no viaja ningún id. El token de
 * Bsale solo va en el PUT y nunca vuelve; el GET informa `tokenSet`.
 *
 * Mismo criterio que el resto de esta capa: se devuelve `{ error }` (siempre
 * texto) en vez de lanzar, para que la sección decida cómo mostrarlo.
 */

const RUTA = "integrations/fiscal/bsale";

// AdminOnlyGuard responde 403 con {message:'Forbidden'}, que el helper descarta
// por genérico: sin texto propio el usuario no sabría que es un tema de rol.
// El 404 del catálogo significa "sin cuenta conectada o desactivada", no un fallo.
const falla = (err) => ({
  error: apiErrorMessage(
    err,
    apiErrorStatus(err) === 403
      ? i18n.t("No tienes permisos para administrar la conexión con Bsale")
      : i18n.t("No se pudo completar la operación"),
  ),
  status: apiErrorStatus(err),
});

/** `{ configured, tokenSet, environment, officeId, isActive, tokenUpdatedAt, lastCheck, updatedAt }` */
export const getBsaleConnection = async () => {
  try {
    const { data } = await Axios.get(`${RUTA}/config`);
    return data;
  } catch (err) {
    return falla(err);
  }
};

/**
 * Guardado parcial: solo viajan los campos presentes. Un token o sucursal nuevos
 * se verifican contra Bsale antes de guardarse (400 si Bsale los rechaza, 503 si
 * no responde): el mensaje del backend ya viene listo para el operador.
 */
export const saveBsaleConnection = async ({
  accessToken,
  officeId,
  environment,
  isActive,
  testMode,
  testDocumentTypeId,
  testCreditNoteDocumentTypeId,
}) => {
  const body = {};
  if (accessToken) body.accessToken = accessToken;
  if (officeId !== undefined) body.officeId = officeId;
  if (environment !== undefined) body.environment = environment;
  if (isActive !== undefined) body.isActive = isActive;
  if (testMode !== undefined) body.testMode = testMode;
  if (testDocumentTypeId !== undefined) body.testDocumentTypeId = testDocumentTypeId;
  if (testCreditNoteDocumentTypeId !== undefined) {
    body.testCreditNoteDocumentTypeId = testCreditNoteDocumentTypeId;
  }
  try {
    const { data } = await Axios.put(`${RUTA}/config`, body);
    return data;
  } catch (err) {
    return falla(err);
  }
};

/** `{ ok, kind?, message?, warnings, catalog? }`: una falla de Bsale llega como ok=false, no como error HTTP. */
export const testBsaleConnection = async () => {
  try {
    const { data } = await Axios.post(`${RUTA}/config/test`);
    return data;
  } catch (err) {
    return falla(err);
  }
};

/** Tipos de documento 33/34/61, IVA, sucursales y avisos de lo que falta para emitir. */
export const getBsaleCatalog = async () => {
  try {
    const { data } = await Axios.get(`${RUTA}/catalog`);
    return data;
  } catch (err) {
    return falla(err);
  }
};
